import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PortalError, papi, type PortalMe } from './api';
import { usePortalText } from './text';
import { pArea, pInput, pPrimary, pSecondary } from './PortalApp';

type Kind = 'incident' | 'service' | 'change';
type Level = 'low' | 'medium' | 'high';

export default function PortalNew({ me }: { me: PortalMe }) {
  const { slug = '' } = useParams();
  const { t } = usePortalText();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind>('incident');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [impact, setImpact] = useState<Level>('medium');
  const [urgency, setUrgency] = useState<Level>('medium');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await papi<{ number: string }>('/tickets', { method: 'POST', body: JSON.stringify({ kind, subject, description, impact, urgency }) });
      void queryClient.invalidateQueries({ queryKey: ['portal-tickets'] });
      navigate(`/t/${slug}/tickets/${r.number}`);
    } catch (err) {
      setError(err instanceof PortalError ? err.message : t.genericError);
      setBusy(false);
    }
  };

  const levels: Level[] = ['low', 'medium', 'high'];
  const choice = ({ name, value, set, labels }: { name: string; value: Level; set: (l: Level) => void; labels: Record<Level, string> }) => (
    <div className="grid gap-2 sm:grid-cols-3">
      {levels.map(l => (
        <label key={l} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm ${value === l ? 'border-asx-navy bg-[#EEF3F6] font-medium' : 'border-[#CFD8DD] bg-white'}`}>
          <input type="radio" name={name} checked={value === l} onChange={() => set(l)} className="accent-[#0C3A52]" />{labels[l]}
        </label>
      ))}
    </div>
  );

  return (
    <form onSubmit={submit} className="mx-auto flex max-w-3xl flex-col gap-6">
      <div>
        <Link to={`/t/${slug}`} className="text-sm text-[#3D6B8A] hover:underline">{t.back}</Link>
        <h1 className="mt-2 text-2xl font-semibold">{t.newTicket}</h1>
        <p className="mt-1 text-sm text-[#5B6B73]">{me.organizationName}</p>
      </div>
      {error && <p role="alert" className="rounded-lg bg-[#FDECEC] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-semibold">{t.whatDoYouNeed}</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {(['incident', 'service', 'change'] as Kind[]).map(k => (
            <label key={k} className={`flex cursor-pointer flex-col gap-1 rounded-xl border p-4 ${kind === k ? 'border-asx-navy bg-white ring-2 ring-asx-navy/15' : 'border-[#E1E7E9] bg-white hover:border-[#B9C9D1]'}`}>
              <span className="flex items-center gap-2 font-semibold">
                <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} className="accent-[#0C3A52]" />{t.kinds[k][0]}
              </span>
              <span className="text-xs leading-relaxed text-[#5B6B73]">{t.kinds[k][1]}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex flex-col gap-1.5 text-sm font-semibold">{t.subject}
        <input required minLength={5} maxLength={200} className={pInput} placeholder={t.subjectHint} value={subject} onChange={e => setSubject(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1.5 text-sm font-semibold">{t.description}
        <textarea required minLength={10} rows={7} className={pArea} value={description} onChange={e => setDescription(e.target.value)} />
        <span className="text-xs font-normal text-[#5B6B73]">{t.descriptionHint}</span>
      </label>
      {kind !== 'change' && (
        <>
          <fieldset><legend className="mb-2 text-sm font-semibold">{t.impact}</legend>{choice({ name: 'impact', value: impact, set: setImpact, labels: t.impactOpts })}</fieldset>
          <fieldset><legend className="mb-2 text-sm font-semibold">{t.urgency}</legend>{choice({ name: 'urgency', value: urgency, set: setUrgency, labels: t.urgencyOpts })}</fieldset>
        </>
      )}
      <div className="flex gap-2">
        <button type="submit" className={pPrimary} disabled={busy}>{busy ? t.creating : t.create}</button>
        <Link to={`/t/${slug}`} className={pSecondary}>{t.cancel}</Link>
      </div>
    </form>
  );
}
